import dayjs, { Dayjs } from 'dayjs'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Typogaphy } from 'vnetwork-ui'
import { TicketStatusPeriod } from '@/modules/Ticket/types/response'

dayjs.extend(quarterOfYear)

interface TicketColumChartProps {
    ticket_status_count_by_period: TicketStatusPeriod[]
    value: 'day' | 'week' | 'month' | 'quarter'
    setValue: (val: 'day' | 'week' | 'month' | 'quarter') => void
    filterTime?: [Dayjs | null, Dayjs | null]
    'data-tour'?: string
}

// Calculate available options based on date range
const getAvailableOptions = (filterTime?: [Dayjs | null, Dayjs | null]) => {
    if (!filterTime || !filterTime[0] || !filterTime[1]) {
        return { day: true, week: true, month: true, quarter: true }
    }
    const [start, end] = filterTime
    const rangeDays = end.diff(start, 'day')

    return {
        day: true, // always available
        week: rangeDays >= 7, // need at least 7 days
        month: rangeDays >= 28 * 2, // need at least ~2 month
        quarter: rangeDays >= 90, // need at least ~3 months
    }
}

export function groupStatsByTimeRange(
    data: TicketStatusPeriod[] = [],
    options: { groupBy: 'week' | 'month' | 'quarter' }
): TicketStatusPeriod[] {
    if (data.length < 2) return data

    const sorted = data
        .map((item) => ({ ...item, date: dayjs(item.period, 'YYYY-MM-DD') }))
        .sort((a, b) => a.date.unix() - b.date.unix())

    const rangeDays = sorted[sorted.length - 1].date.diff(sorted[0].date, 'day')
    if (rangeDays < 7) return sorted

    const groupedMap = new Map<
        string,
        { open_tickets: number; closed_tickets: number }
    >()

    for (const item of sorted) {
        const d = item.date
        let key = ''

        switch (options.groupBy) {
            case 'week':
                key = `${d.startOf('isoWeek').format('DD/MM')} - ${d.endOf('isoWeek').format('DD/MM/YYYY')}`
                break
            case 'month':
                key = d.format('MM/YYYY')
                break
            case 'quarter':
                key = `Q${d.quarter()} - ${d.year()}`
                break
        }

        if (!groupedMap.has(key))
            groupedMap.set(key, { open_tickets: 0, closed_tickets: 0 })
        const current = groupedMap.get(key)!
        current.open_tickets += item.open_tickets
        current.closed_tickets += item.closed_tickets
    }

    return Array.from(groupedMap, ([period, vals]) => ({
        period,
        open_tickets: vals.open_tickets,
        closed_tickets: vals.closed_tickets,
    }))
}

const COLORS = {
    opened: '#3b82f6',
    closed: '#10b981',
}

const TicketColumChart: React.FC<TicketColumChartProps> = ({
    ticket_status_count_by_period,
    value,
    setValue,
    filterTime,
    'data-tour': dataTour,
}) => {
    const { t } = useTranslation('ticket')

    const availableOptions = useMemo(
        () => getAvailableOptions(filterTime),
        [filterTime]
    )

    const periodOptions = [
        { label: t('day'), value: 'day' },
        { label: t('week'), value: 'week' },
        { label: t('month'), value: 'month' },
        { label: t('quarter'), value: 'quarter' },
    ] as const

    const chartData = useMemo(() => {
        const rawData =
            value === 'day'
                ? ticket_status_count_by_period || []
                : groupStatsByTimeRange(ticket_status_count_by_period, {
                      groupBy: value,
                  }) || []

        return rawData.map((item) => ({
            ...item,
            label:
                value === 'day'
                    ? dayjs(item.period).format('DD/MM')
                    : item.period,
        }))
    }, [ticket_status_count_by_period, value])

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload || !payload.length) return null

        return (
            <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                <p className="font-semibold text-gray-800 mb-2">{label}</p>
                <div className="space-y-1 text-sm">
                    <p style={{ color: COLORS.opened }}>
                        {t('opened')}:{' '}
                        <span className="font-medium">
                            {payload[0]?.value || 0}
                        </span>
                    </p>
                    <p style={{ color: COLORS.closed }}>
                        {t('closed')}:{' '}
                        <span className="font-medium">
                            {payload[1]?.value || 0}
                        </span>
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div
            className="bg-white rounded-[8px] p-4 lg:col-span-4 col-span-12 half-col-print"
            data-tour={dataTour}
        >
            <div>
                <Typogaphy
                    size="small"
                    className="text-primary font-semibold capitalize"
                >
                    {t('ticket-count', {
                        open: ticket_status_count_by_period?.reduce(
                            (acc, item) => acc + item.open_tickets,
                            0
                        ),
                        closed: ticket_status_count_by_period?.reduce(
                            (acc, item) => acc + item.closed_tickets,
                            0
                        ),
                    })}
                </Typogaphy>

                <div className="mt-2">
                    <div className="inline-flex rounded-md bg-gray-100 p-0.5">
                        {periodOptions.map((opt) => {
                            const isDisabled =
                                !availableOptions[
                                    opt.value as keyof typeof availableOptions
                                ]
                            const isActive = opt.value === value
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => setValue(opt.value)}
                                    disabled={isDisabled}
                                    className={`
                                        px-3 py-1 text-xs font-medium rounded transition-all
                                        ${
                                            isActive
                                                ? 'bg-white text-primary shadow-sm'
                                                : 'text-gray-500 hover:text-gray-700'
                                        }
                                        ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                                    `}
                                >
                                    {opt.label}
                                </button>
                            )
                        })}
                    </div>
                </div>
            </div>

            {chartData.length === 0 ? (
                <div className="flex justify-center items-center h-[300px] text-gray-500">
                    {t('no-data')}
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart
                        data={chartData}
                        margin={{ top: 20, right: 10, left: -20, bottom: 5 }}
                    >
                        <defs>
                            <linearGradient
                                id="colorOpened"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="5%"
                                    stopColor={COLORS.opened}
                                    stopOpacity={0.3}
                                />
                                <stop
                                    offset="95%"
                                    stopColor={COLORS.opened}
                                    stopOpacity={0}
                                />
                            </linearGradient>
                            <linearGradient
                                id="colorClosed"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="5%"
                                    stopColor={COLORS.closed}
                                    stopOpacity={0.3}
                                />
                                <stop
                                    offset="95%"
                                    stopColor={COLORS.closed}
                                    stopOpacity={0}
                                />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                            dataKey="label"
                            stroke="#9ca3af"
                            fontSize={10}
                            tickLine={false}
                            angle={chartData.length > 7 ? -45 : 0}
                            textAnchor={chartData.length > 7 ? 'end' : 'middle'}
                            height={chartData.length > 7 ? 60 : 30}
                        />
                        <YAxis
                            stroke="#9ca3af"
                            fontSize={10}
                            tickLine={false}
                            axisLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            verticalAlign="bottom"
                            height={24}
                            content={({ payload }) => (
                                <div className="flex justify-center gap-6">
                                    {payload?.map((entry, index) => (
                                        <div
                                            key={`legend-${index}`}
                                            className="flex items-center gap-1.5"
                                        >
                                            <svg
                                                width="16"
                                                height="10"
                                                viewBox="0 0 16 10"
                                            >
                                                <line
                                                    x1="0"
                                                    y1="5"
                                                    x2="16"
                                                    y2="5"
                                                    stroke={entry.color}
                                                    strokeWidth="2"
                                                />
                                            </svg>
                                            <span className="text-xs text-gray-600">
                                                {entry.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        />

                        {/* Opened tickets area */}
                        <Area
                            type="monotone"
                            dataKey="open_tickets"
                            name={t('opened')}
                            stroke={COLORS.opened}
                            strokeWidth={1}
                            fill="url(#colorOpened)"
                            dot={{
                                fill: COLORS.opened,
                                strokeWidth: 1,
                                r: 1,
                                stroke: '#fff',
                            }}
                            label={(props: any) =>
                                props.value > 0 ? (
                                    <text
                                        x={props.x}
                                        y={props.y - 8}
                                        fill={COLORS.opened}
                                        fontSize={10}
                                        textAnchor="middle"
                                    >
                                        {props.value}
                                    </text>
                                ) : null
                            }
                        />

                        {/* Closed tickets line */}
                        <Line
                            type="monotone"
                            dataKey="closed_tickets"
                            name={t('closed')}
                            stroke={COLORS.closed}
                            strokeWidth={1.5}
                            dot={{
                                fill: COLORS.closed,
                                strokeWidth: 1.5,
                                r: 3,
                                stroke: '#fff',
                            }}
                            label={(props: any) =>
                                props.value > 0 ? (
                                    <text
                                        x={props.x}
                                        y={props.y + 16}
                                        fill={COLORS.closed}
                                        fontSize={10}
                                        fontWeight="bold"
                                        textAnchor="middle"
                                    >
                                        {props.value}
                                    </text>
                                ) : null
                            }
                        />
                    </AreaChart>
                </ResponsiveContainer>
            )}
        </div>
    )
}

export default TicketColumChart
